import React from 'react';
import { 
  Shield, TrendingUp, TrendingDown, Sparkles, 
  CheckCircle2, AlertCircle, Flame, Users,
  Target, Brain, LineChart, GitBranch, ClipboardCheck,
  Activity, Zap
} from 'lucide-react';
import { DESIGN } from '../DesignSystem';

// Chief of Staff Premium Colors
const COS_COLORS = {
  primary: '#7B4FE8',
  primaryDark: '#5E35B1',
  primaryGlow: 'rgba(123, 79, 232, 0.4)',
  primarySubtle: 'rgba(123, 79, 232, 0.08)',
  heroBg: '#1E1B2E',
  heroGradient: 'linear-gradient(90deg, rgba(123, 79, 232, 0.15) 0%, transparent 50%)',
  cardBorder: 'rgba(123, 79, 232, 0.2)',
  accentGreen: '#10B981',
  accentAmber: '#F59E0B',
  accentRed: '#EF4444'
};

// ================================================
// SPARKLINE CHART COMPONENT
// ================================================
const Sparkline = ({ data, color, width = 60, height = 24, trending }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((value - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  
  const trendColor = trending === 'up' ? COS_COLORS.accentGreen : 
                     trending === 'down' ? COS_COLORS.accentRed : color;
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={trendColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ filter: `drop-shadow(0 0 3px ${trendColor}50)` }}
      />
      {/* End dot */}
      <circle
        cx={width}
        cy={height - ((data[data.length - 1] - min) / range) * (height - 4) - 2}
        r="3"
        fill={trendColor}
        style={{ filter: `drop-shadow(0 0 4px ${trendColor})` }}
      />
    </svg>
  );
};

// ================================================
// HERO BANNER - War Room Command Center
// ================================================
export const CommandCenterHeroBanner = () => {
  const pulseIndicators = [
    { label: 'Executive Response Active', status: 'active', color: COS_COLORS.accentGreen },
    { label: '2 Escalations Pending', status: 'warning', color: COS_COLORS.accentAmber },
    { label: 'Board Briefing Due in 6h', status: 'urgent', color: COS_COLORS.accentRed }
  ];

  return (
    <div 
      className="relative w-full rounded-2xl overflow-hidden"
      style={{ 
        backgroundColor: COS_COLORS.heroBg,
        boxShadow: `0 8px 32px rgba(0,0,0,0.3), 0 0 60px ${COS_COLORS.primaryGlow}`
      }}
    >
      {/* Purple gradient from left */}
      <div 
        className="absolute inset-0"
        style={{ background: COS_COLORS.heroGradient }}
      />
      
      {/* Subtle grid pattern */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `linear-gradient(rgba(123, 79, 232, 0.3) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(123, 79, 232, 0.3) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}
      />
      
      <div className="relative flex items-center justify-between p-8">
        {/* Left side - Icon and Title */}
        <div className="flex items-center gap-6">
          {/* Glowing shield icon */}
          <div className="relative">
            <div 
              className="absolute inset-0 rounded-full blur-xl"
              style={{ 
                backgroundColor: COS_COLORS.primary,
                opacity: 0.5,
                transform: 'scale(1.5)'
              }}
            />
            <div 
              className="relative w-20 h-20 rounded-full flex items-center justify-center"
              style={{ 
                backgroundColor: COS_COLORS.primary,
                boxShadow: `0 0 30px ${COS_COLORS.primaryGlow}, inset 0 2px 10px rgba(255,255,255,0.2)`
              }}
            >
              <Shield className="w-10 h-10 text-white" />
            </div>
          </div>
          
          <div>
            <h1 className="text-3xl font-black text-white tracking-tight">
              Chief of Staff Command Center
            </h1>
            <p className="text-base mt-1" style={{ color: 'rgba(167, 139, 250, 0.8)' }}>
              Organization-Wide Coordination Intelligence
            </p>
          </div>
        </div>
        
        {/* Right side - Live pulse indicators */}
        <div className="flex flex-col gap-3">
          {pulseIndicators.map((indicator, idx) => (
            <div 
              key={idx}
              className="flex items-center gap-3 px-4 py-2 rounded-lg"
              style={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
            >
              {/* Animated pulse dot */}
              <div className="relative">
                <div 
                  className="w-2.5 h-2.5 rounded-full animate-pulse"
                  style={{ backgroundColor: indicator.color }}
                />
                <div 
                  className="absolute inset-0 w-2.5 h-2.5 rounded-full animate-ping"
                  style={{ backgroundColor: indicator.color, opacity: 0.4 }}
                />
              </div>
              <span className="text-sm font-medium text-white whitespace-nowrap">
                {indicator.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ================================================
// PREMIUM STAT CARD WITH SPARKLINE
// ================================================
export const PremiumStatCard = ({ label, value, unit, trend, trendValue, sparklineData, trendDirection }) => {
  const getTrendColor = () => {
    // For "good when down" metrics like escalations
    if (trendDirection === 'down-is-good') {
      return trend === 'down' ? COS_COLORS.accentGreen : COS_COLORS.accentRed;
    }
    // For "good when up" metrics like time protected
    return trend === 'up' ? COS_COLORS.accentGreen : 
           trend === 'down' ? COS_COLORS.accentRed : 
           DESIGN.colors.textMuted;
  };

  return (
    <div 
      className="relative p-5 rounded-xl overflow-hidden"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        borderTop: `3px solid ${COS_COLORS.primary}`,
        boxShadow: DESIGN.shadows.md
      }}
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <p className="text-sm font-medium mb-2" style={{ color: DESIGN.colors.textSecondary }}>
            {label}
          </p>
          <div className="flex items-baseline gap-2">
            <span 
              className="text-4xl font-black"
              style={{ color: DESIGN.colors.textPrimary }}
            >
              {value}{unit === '%' ? unit : ''}
            </span>
            {trendValue !== undefined && (
              <span 
                className="flex items-center gap-1 text-sm font-semibold"
                style={{ color: getTrendColor() }}
              >
                {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : 
                 trend === 'down' ? <TrendingDown className="w-4 h-4" /> : null}
                {trendValue > 0 ? '+' : ''}{trendValue}
              </span>
            )}
          </div>
        </div>
        
        {/* Sparkline in bottom right */}
        {sparklineData && (
          <div className="absolute bottom-3 right-3 opacity-70">
            <Sparkline 
              data={sparklineData} 
              color={COS_COLORS.primary}
              width={60}
              height={28}
              trending={trend}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ================================================
// ORGANIZATION HEALTH RING CHART
// ================================================
export const OrganizationHealthRing = ({ overall, execution, teamHealth, delivery }) => {
  const radius = 60;
  const strokeWidth = 12;
  const circumference = 2 * Math.PI * radius;
  const progress = (overall / 100) * circumference;
  
  return (
    <div 
      className="p-6 rounded-xl h-full"
      style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
    >
      <h3 className="text-base font-bold mb-4" style={{ color: DESIGN.colors.textPrimary }}>
        Organization Health
      </h3>
      
      <div className="flex items-center gap-6">
        {/* Main ring */}
        <div className="relative">
          <svg width="140" height="140" className="transform -rotate-90">
            {/* Background ring */}
            <circle
              cx="70"
              cy="70"
              r={radius}
              fill="none"
              stroke={DESIGN.colors.border}
              strokeWidth={strokeWidth}
            />
            {/* Progress ring with gradient */}
            <defs>
              <linearGradient id="healthGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={COS_COLORS.primary} />
                <stop offset="100%" stopColor="#A78BFA" />
              </linearGradient>
            </defs>
            <circle
              cx="70"
              cy="70"
              r={radius}
              fill="none"
              stroke="url(#healthGradient)"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference - progress}
              style={{ 
                filter: `drop-shadow(0 0 8px ${COS_COLORS.primaryGlow})`,
                transition: 'stroke-dashoffset 1s ease-out'
              }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span 
              className="text-4xl font-black"
              style={{ color: COS_COLORS.primary }}
            >
              {overall}
            </span>
            <span className="text-xs font-medium" style={{ color: DESIGN.colors.textMuted }}>
              Overall
            </span>
          </div>
        </div>
        
        {/* Sub-scores */}
        <div className="space-y-3">
          {[
            { label: 'Execution', value: execution, color: '#3B82F6' },
            { label: 'Team Health', value: teamHealth, color: '#10B981' },
            { label: 'Delivery', value: delivery, color: '#F59E0B' }
          ].map((item) => (
            <div key={item.label} className="flex items-center gap-3">
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
                {item.label}
              </span>
              <span 
                className="text-sm font-bold ml-auto"
                style={{ color: DESIGN.colors.textPrimary }}
              >
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ================================================
// SOUL FOOD BRIDGE CARD
// ================================================
export const SoulFoodBridgeCard = ({ mainCompanyHealth, soulFoodHealth }) => {
  return (
    <div 
      className="p-6 rounded-xl h-full"
      style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
    >
      <h3 className="text-base font-bold mb-4" style={{ color: DESIGN.colors.textPrimary }}>
        Soul Food Bridge
      </h3>
      
      <div className="flex items-center justify-between gap-4">
        {/* Main Company */}
        <div 
          className="flex-1 p-4 rounded-xl text-center"
          style={{ 
            backgroundColor: `${COS_COLORS.primary}10`,
            border: `2px solid ${COS_COLORS.primary}30`
          }}
        >
          <p className="text-xs font-medium mb-1" style={{ color: COS_COLORS.primary }}>
            Main Company
          </p>
          <p 
            className="text-3xl font-black"
            style={{ color: COS_COLORS.primary }}
          >
            {mainCompanyHealth}
          </p>
        </div>
        
        {/* Bridge connector */}
        <div className="relative flex items-center">
          <div 
            className="w-12 h-1"
            style={{ 
              background: `linear-gradient(90deg, ${COS_COLORS.primary}, #F59E0B)`,
              boxShadow: '0 0 10px rgba(123, 79, 232, 0.3)'
            }}
          />
          <div 
            className="absolute left-1/2 -translate-x-1/2 w-3 h-3 rounded-full"
            style={{ 
              background: 'linear-gradient(135deg, #7B4FE8, #F59E0B)',
              boxShadow: '0 0 8px rgba(123, 79, 232, 0.5)'
            }}
          />
        </div>
        
        {/* Soul Food */}
        <div 
          className="flex-1 p-4 rounded-xl text-center"
          style={{ 
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            border: '2px solid rgba(245, 158, 11, 0.3)'
          }}
        >
          <p className="text-xs font-medium mb-1" style={{ color: '#F59E0B' }}>
            Soul Food
          </p>
          <p 
            className="text-3xl font-black"
            style={{ color: '#F59E0B' }}
          >
            {soulFoodHealth}
          </p>
        </div>
      </div>
    </div>
  );
};

// ================================================
// COMMAND PRIORITIES CARD
// ================================================
export const CommandPrioritiesCard = ({ priorities }) => {
  const urgencyConfig = {
    critical: { color: COS_COLORS.accentRed, bg: 'rgba(239, 68, 68, 0.1)' },
    high: { color: COS_COLORS.accentAmber, bg: 'rgba(245, 158, 11, 0.1)' },
    medium: { color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.1)' }
  };

  const defaultPriorities = priorities || [
    { text: 'Resolve West Region burnout escalation', urgency: 'critical' },
    { text: 'Finalize Q2 Marketing briefing', urgency: 'high' },
    { text: 'Review Nike partnership contract', urgency: 'medium' }
  ];

  return (
    <div 
      className="p-6 rounded-xl h-full"
      style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
    >
      <h3 className="text-base font-bold mb-4" style={{ color: DESIGN.colors.textPrimary }}>
        Command Priorities
      </h3>
      
      <div className="space-y-3">
        {defaultPriorities.map((item, idx) => {
          const config = urgencyConfig[item.urgency] || urgencyConfig.medium;
          return (
            <div 
              key={idx}
              className="flex items-start gap-3 p-3 rounded-lg"
              style={{ backgroundColor: config.bg }}
            >
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                style={{ backgroundColor: config.color }}
              >
                {idx + 1}
              </div>
              <p className="text-sm" style={{ color: DESIGN.colors.textPrimary }}>
                {item.text}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ================================================
// COORDINATION LOAD BY DEPARTMENT (Horizontal Bar Chart)
// ================================================
export const CoordinationLoadChart = ({ data }) => {
  const defaultData = data || [
    { department: 'Marketing', percentage: 32, count: 8 },
    { department: 'Operations', percentage: 24, count: 6 },
    { department: 'Engineering', percentage: 20, count: 5 },
    { department: 'Soul Food', percentage: 12, count: 3 },
    { department: 'Finance', percentage: 12, count: 3 }
  ];

  return (
    <div 
      className="p-6 rounded-xl"
      style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
    >
      <h3 className="text-base font-bold mb-5" style={{ color: DESIGN.colors.textPrimary }}>
        Coordination Load by Department
      </h3>
      
      <div className="space-y-4">
        {defaultData.map((item, idx) => (
          <div key={idx}>
            <div className="flex justify-between mb-1.5">
              <span className="text-sm font-medium" style={{ color: DESIGN.colors.textPrimary }}>
                {item.department}
              </span>
              <span className="text-sm font-bold" style={{ color: COS_COLORS.primary }}>
                {item.percentage}%
              </span>
            </div>
            <div 
              className="h-3 rounded-full overflow-hidden"
              style={{ backgroundColor: DESIGN.colors.background }}
            >
              <div 
                className="h-full rounded-full transition-all duration-700"
                style={{ 
                  width: `${item.percentage}%`,
                  background: `linear-gradient(90deg, ${COS_COLORS.primary}CC, ${COS_COLORS.primary})`,
                  boxShadow: `0 0 10px ${COS_COLORS.primaryGlow}`
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================
// DECISION BACKLOG TREND (Bar Chart with Trendline)
// ================================================
export const DecisionBacklogTrendChart = ({ data }) => {
  const defaultData = data || [
    { month: 'Aug', backlog: 14 },
    { month: 'Sep', backlog: 12 },
    { month: 'Oct', backlog: 10 },
    { month: 'Nov', backlog: 8 },
    { month: 'Dec', backlog: 5 }
  ];

  const maxBacklog = Math.max(...defaultData.map(d => d.backlog));

  return (
    <div 
      className="p-6 rounded-xl"
      style={{ backgroundColor: DESIGN.colors.card, boxShadow: DESIGN.shadows.md }}
    >
      <h3 className="text-base font-bold mb-5" style={{ color: DESIGN.colors.textPrimary }}>
        Decision Backlog Trend
      </h3>
      
      <div className="relative">
        {/* Bars */}
        <div className="flex items-end gap-3 h-32 mb-3">
          {defaultData.map((item, idx) => {
            const height = (item.backlog / maxBacklog) * 100;
            return (
              <div key={idx} className="flex-1 flex flex-col items-center">
                <div 
                  className="w-full rounded-t transition-all duration-500 relative"
                  style={{ 
                    height: `${height}%`,
                    background: `linear-gradient(180deg, ${COS_COLORS.primary} 0%, ${COS_COLORS.primaryDark} 100%)`,
                    boxShadow: `0 0 10px ${COS_COLORS.primaryGlow}`
                  }}
                >
                  <span 
                    className="absolute -top-5 left-1/2 -translate-x-1/2 text-xs font-bold"
                    style={{ color: COS_COLORS.primary }}
                  >
                    {item.backlog}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Trendline overlay - positioned relative to container */}
        <svg 
          className="absolute inset-0 pointer-events-none" 
          style={{ top: '0', height: '128px', width: '100%' }}
          viewBox="0 0 100 128"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="trendGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#10B981" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#10B981" stopOpacity="1" />
            </linearGradient>
          </defs>
          <polyline
            points={defaultData.map((item, idx) => {
              const x = (idx / (defaultData.length - 1)) * 100;
              const y = 128 - (item.backlog / maxBacklog) * 128;
              return `${x},${y}`;
            }).join(' ')}
            fill="none"
            stroke="url(#trendGradient)"
            strokeWidth="2"
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
            style={{ filter: 'drop-shadow(0 0 4px #10B98180)' }}
          />
        </svg>
        
        {/* X-axis labels */}
        <div className="flex gap-3">
          {defaultData.map((item, idx) => (
            <div key={idx} className="flex-1 text-center">
              <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
                {item.month}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ================================================
// ENHANCED HOTSPOT ROW WITH AI INSIGHT
// ================================================
export const EnhancedHotspotRow = ({ person, isExecPriority }) => {
  const levelConfig = {
    critical: { color: COS_COLORS.accentRed, bg: 'rgba(239, 68, 68, 0.08)' },
    high: { color: COS_COLORS.accentAmber, bg: 'rgba(245, 158, 11, 0.08)' },
    elevated: { color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.08)' }
  };

  const config = levelConfig[person.level] || levelConfig.elevated;

  // AI-generated insights based on person data
  const aiInsights = {
    'James Chen': 'Lane misalignment detected — Operations work assigned to Strategy-primary thinker',
    'Jessica Park': 'High context-switching frequency — 6 different project types this week',
    'Elena Rodriguez': 'Capacity exceeded — Managing 12 concurrent initiatives',
    'David Kim': 'Bottleneck risk — Critical path dependency on 3 projects'
  };

  return (
    <div 
      className="p-4 rounded-xl transition-all"
      style={{ 
        backgroundColor: DESIGN.colors.background,
        borderLeft: `4px solid ${COS_COLORS.primary}`
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-white"
            style={{ backgroundColor: COS_COLORS.primary }}
          >
            {person.avatar}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-bold" style={{ color: DESIGN.colors.textPrimary }}>
                {person.name}
              </p>
              {isExecPriority && (
                <span 
                  className="relative px-2.5 py-1 rounded-full text-xs font-bold text-white flex items-center gap-1.5"
                  style={{ backgroundColor: COS_COLORS.primary }}
                >
                  <span className="relative flex h-2 w-2">
                    <span 
                      className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                      style={{ backgroundColor: 'white' }}
                    />
                    <span 
                      className="relative inline-flex rounded-full h-2 w-2"
                      style={{ backgroundColor: 'white' }}
                    />
                  </span>
                  Exec Priority
                </span>
              )}
            </div>
            <p className="text-sm" style={{ color: DESIGN.colors.textSecondary }}>
              {person.role}
            </p>
            {/* AI Insight */}
            <p 
              className="text-xs italic mt-1"
              style={{ color: DESIGN.colors.textMuted }}
            >
              {aiInsights[person.name] || 'Workload analysis in progress...'}
            </p>
          </div>
        </div>
        
        <div 
          className="text-2xl font-black"
          style={{ color: config.color }}
        >
          {person.score}%
        </div>
      </div>
    </div>
  );
};

// ================================================
// COS EFFECTIVENESS SCORE
// ================================================
export const COSEffectivenessScore = ({ score, percentile }) => {
  return (
    <div 
      className="p-6 rounded-xl text-center"
      style={{ 
        background: `linear-gradient(135deg, ${COS_COLORS.heroBg} 0%, #2D2640 100%)`,
        border: `2px solid ${COS_COLORS.primary}40`,
        boxShadow: `0 0 30px ${COS_COLORS.primaryGlow}`
      }}
    >
      <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: COS_COLORS.primary }}>
        CoS Effectiveness
      </p>
      <p className="text-6xl font-black text-white mb-2">
        {score}
      </p>
      <p className="text-sm" style={{ color: 'rgba(167, 139, 250, 0.8)' }}>
        Top {percentile}% of coordination roles
      </p>
    </div>
  );
};

// ================================================
// COORDINATION DEMAND HEATMAP (Enhanced)
// ================================================
export const CoordinationDemandHeatmap = ({ data }) => {
  const defaultData = data || {
    monday: { demand: 82 },
    tuesday: { demand: 68 },
    wednesday: { demand: 75 },
    thursday: { demand: 58 },
    friday: { demand: 92 }
  };

  const getDemandColor = (demand) => {
    if (demand >= 80) return COS_COLORS.accentRed;
    if (demand >= 60) return COS_COLORS.accentAmber;
    return COS_COLORS.accentGreen;
  };

  const getDemandBg = (demand) => {
    if (demand >= 80) return 'rgba(239, 68, 68, 0.15)';
    if (demand >= 60) return 'rgba(245, 158, 11, 0.15)';
    return 'rgba(16, 185, 129, 0.15)';
  };

  return (
    <div 
      className="p-6 rounded-xl"
      style={{ 
        backgroundColor: DESIGN.colors.card, 
        boxShadow: DESIGN.shadows.md,
        border: `1px solid ${COS_COLORS.cardBorder}`
      }}
    >
      <h3 className="text-base font-bold mb-5" style={{ color: DESIGN.colors.textPrimary }}>
        Coordination Demand by Day
      </h3>
      
      <div className="flex gap-3 h-40">
        {Object.entries(defaultData).map(([day, info]) => {
          const color = getDemandColor(info.demand);
          const bg = getDemandBg(info.demand);
          return (
            <div key={day} className="flex-1 flex flex-col">
              <div 
                className="flex-1 rounded-xl flex flex-col items-center justify-center transition-all hover:scale-105 cursor-pointer"
                style={{ 
                  backgroundColor: bg,
                  border: `2px solid ${color}40`,
                  boxShadow: info.demand >= 80 ? `0 0 15px ${color}30` : 'none'
                }}
              >
                <span 
                  className="text-3xl font-black"
                  style={{ color }}
                >
                  {info.demand}%
                </span>
                <span className="text-xs mt-1" style={{ color: DESIGN.colors.textMuted }}>
                  {info.demand >= 80 ? 'High' : info.demand >= 60 ? 'Moderate' : 'Low'}
                </span>
              </div>
              <p 
                className="text-center text-xs font-medium mt-2 capitalize"
                style={{ color: DESIGN.colors.textSecondary }}
              >
                {day.slice(0, 3)}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ================================================
// PREMIUM TAB NAVIGATION FOR COS
// ================================================
export const COSTabNavigation = ({ tabs, activeTab, onTabChange }) => {
  return (
    <div 
      className="flex gap-2 p-1.5 rounded-xl overflow-x-auto"
      style={{ backgroundColor: DESIGN.colors.background }}
    >
      {tabs.map(tab => {
        const Icon = tab.icon;
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${isActive ? 'shadow-lg' : 'hover:bg-white/50'}`}
            style={{
              backgroundColor: isActive ? COS_COLORS.primary : 'transparent',
              color: isActive ? 'white' : DESIGN.colors.textSecondary,
              boxShadow: isActive ? `0 0 15px ${COS_COLORS.primaryGlow}` : 'none'
            }}
          >
            <Icon className="w-4 h-4" />
            {tab.label}
            {tab.count !== undefined && (
              <span 
                className="ml-1 px-1.5 py-0.5 rounded-full text-xs"
                style={{ 
                  backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : COS_COLORS.primarySubtle,
                  color: isActive ? 'white' : COS_COLORS.primary
                }}
              >
                {tab.count}
              </span>
            )}
            {tab.exclusive && (
              <span 
                className="ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold"
                style={{ 
                  backgroundColor: isActive ? 'rgba(255,255,255,0.2)' : COS_COLORS.primary,
                  color: 'white'
                }}
              >
                Exclusive
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export { COS_COLORS };
